// Single source of truth for progress/risk/news iconography. The observation
// create/edit screen (ObservationTypeSelector) is the canon this module
// mirrors: circled check for progress, circled exclamation for risk, circled
// newspaper for news. Every other surface — map pins (native + web),
// structure badges, onboarding legend, hover card — sources from here
// instead of hand-picking a lucide icon or copying SVG path data by value.
import { CheckCircle, AlertCircle, Newspaper, type LucideIcon } from 'lucide-react-native';
import type { PostCategory } from '../types';

// For React trees: selectors, badges, legends.
export const CATEGORY_ICONS: Record<PostCategory, LucideIcon> = {
  progress: CheckCircle,
  risk: AlertCircle,
  news: Newspaper,
};

// Bare inner-glyph SVG path data (24 viewBox), for use inside colored pin
// dots where the dot itself acts as the circle — one path per array entry,
// meant to be stroked (not filled) at strokeWidth 3 with round caps.
export const CATEGORY_GLYPH_PATHS: Record<PostCategory, string[]> = {
  // lucide Check path, unchanged from the pre-existing marker glyph.
  progress: ['M20 6 9 17l-5-5'],
  // Exclamation as two subpaths: a short vertical stroke plus a "dot" drawn
  // as a zero-length segment — the round linecap renders it as a circle.
  risk: ['M12 4.5v9', 'M12 19h.01'],
  // lucide Newspaper's outline plus its two body-text lines; the small
  // masthead rect lucide draws at the top is dropped — it disappears at
  // glyph size anyway and the array already mirrors risk's "drop what's too
  // fine to read small" pattern.
  news: [
    'M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9a2 2 0 0 1 2-2h2',
    'M15 18h-5',
    'M18 14h-8',
  ],
};

// Delta ("before/after") badge triangle, shared by both platforms' map
// markers so no SVG path data stays copied-by-value across files.
export const TRIANGLE_PATH = 'M13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z';

/**
 * Structure (property) marker silhouette, drawn as one filled outline in the
 * same 24 viewBox as the glyphs above.
 *
 * Shape is what tells the map's two vocabularies apart: a property is a
 * house, an observation is a circle, a before/after pair is a triangle.
 * Before this existed both properties and observations drew as colored
 * circles and only the inner glyph separated them, which is not a difference
 * you can see at 24px.
 *
 * Deliberately a plain gabled silhouette with a shallow eave, not a
 * reproduction of the Monopoly piece it was asked to evoke: a solid shape
 * with few corners survives being scaled to marker size, and drawing our own
 * keeps us clear of someone else's game art.
 */
export const HOUSE_PATH = 'M12 2.6 22 11.4h-2.6v10H4.6v-10H2Z';

/**
 * Community program marker silhouette (Firewise USA sites, California fire
 * safe councils), in the same 24 viewBox as the shapes above.
 *
 * A hexagon, because it reads as a seal: these points stand for a community
 * that earned a recognition, not for a property (house) or an observation
 * (circle). Keeping the silhouette distinct is what lets the three
 * vocabularies coexist on one map at the same zoom.
 */
export const COMMUNITY_BADGE_PATH = 'M12 2.2 21 7.1v9.8L12 21.8 3 16.9V7.1Z';
